import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * PromptModal - A custom input modal to replace window.prompt.
 */
export const PromptModal = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    initialValue = "",
    confirmText,
    cancelText,
    loading = false,
}) => {
    const { t } = useTranslation(['ui-complex', 'common']);
    const resolvedTitle = title ?? t('ui-complex:promptModal.title');
    const resolvedConfirmText = confirmText ?? t('common:save');
    const resolvedCancelText = cancelText ?? t('common:cancel');
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
        if (isOpen) {
            setValue(initialValue);
        }
    }, [isOpen, initialValue]);

    if (!isOpen) return null;

    const handleConfirm = () => {
        if (value.trim()) {
            onConfirm(value.trim());
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={loading ? undefined : onClose}>
            <div className="bg-white rounded-xl max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200" onClick={(e) => e.stopPropagation()}>
                <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900">{resolvedTitle}</h3>
                        {!loading && (
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-full">
                                <X className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                    
                    {message && <p className="text-gray-600 text-sm mb-4">{message}</p>}
                    
                    <div className="space-y-4">
                        <input
                            type="text"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 transition-all"
                            placeholder={t('ui-complex:promptModal.placeholder')}
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !loading) handleConfirm();
                                if (e.key === 'Escape' && !loading) onClose();
                            }}
                        />

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={onClose}
                                disabled={loading}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm disabled:opacity-50"
                            >
                                {resolvedCancelText}
                            </button>
                            <button
                                onClick={handleConfirm}
                                disabled={loading || !value.trim()}
                                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all font-medium text-sm disabled:opacity-50 flex items-center gap-2 shadow-sm shadow-primary-200"
                            >
                                {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                                {resolvedConfirmText}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
